import {
  SalesRepGrossProfitValidationErrorCode,
  SalesRepPriceValidationErrorCode,
} from '@platform/errors';
import { useCallback } from 'react';
import { useUpdateSalesRepGrossProfitOverrideMutation } from '@/domain/pricing/blocs/documents/update-sales-rep-gross-profit.generated';

interface CommitGrossProfitParams {
  deliveryDate: string;
  expirationDate: string | null;
  grossProfitInPercentage: number | null;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}

export interface GrossProfitOverrideResult {
  defaultPriceInMicros: number | null;
  priceInMicros: number | null;
  salesRepGrossProfitInPercentage: number | null;
  salesRepPriceInMicros: number | null;
  unitCount: number;
  updatedByName: string | null;
}

/**
 * Mutator hook for saving a gross profit override via
 * `updateSalesRepGrossProfitPercentageOverrides`.
 *
 * @param options.onError - Optional callback invoked on mutation failure
 * @returns `commitGrossProfit(params)` — resolves to mutation response data or null on failure
 *
 * @example
 * ```typescript
 * const { commitGrossProfit } = useGrossProfitOverrideMutator({
 *   onError: () => showErrorToast(),
 * });
 *
 * const result = await commitGrossProfit({
 *   deliveryDate: '2026-03-18',
 *   grossProfitInPercentage: 25.5,
 *   restaurantUUID: 'rest-123',
 *   supplierUUID: 'sup-456',
 *   variantPackUUID: 'vp-789',
 * });
 * ```
 */
export const useGrossProfitOverrideMutator = ({
  onError,
}: { onError?: () => void } = {}) => {
  const [updateGrossProfit] = useUpdateSalesRepGrossProfitOverrideMutation({
    context: {
      ignoreGraphQLErrorCodes: [
        SalesRepGrossProfitValidationErrorCode.SALES_REP_GROSS_PROFIT_TOO_HIGH,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_LOW,
        SalesRepPriceValidationErrorCode.SALES_REP_PRICE_TOO_HIGH,
      ],
    },
  });

  const commitGrossProfit = useCallback(
    async (
      params: CommitGrossProfitParams,
    ): Promise<GrossProfitOverrideResult | null> => {
      try {
        const { data } = await updateGrossProfit({
          variables: {
            deliveryDate: params.deliveryDate,
            expirationDate: params.expirationDate,
            restaurantUUID: params.restaurantUUID,
            supplierUUID: params.supplierUUID,
            variantPacks: [
              {
                gross_profit_in_percentage:
                  params.grossProfitInPercentage?.toString() ?? null,
                variant_pack_id: params.variantPackUUID,
              },
            ],
          },
        });
        const result = data?.updateSalesRepGrossProfitPercentageOverrides?.[0];
        return result
          ? {
              defaultPriceInMicros: result.default_price_in_micros ?? null,
              priceInMicros: result.price_in_micros ?? null,
              salesRepGrossProfitInPercentage:
                result.sales_rep_gross_profit_in_percentage ?? null,
              salesRepPriceInMicros: result.sales_rep_price_in_micros ?? null,
              unitCount: result.unit_count,
              updatedByName: result.sales_rep_metadata?.updated_by_name ?? null,
            }
          : null;
      } catch {
        onError?.();
        return null;
      }
    },
    [onError, updateGrossProfit],
  );

  return { commitGrossProfit };
};
